import { produce } from 'immer'
import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { useCallback, useMemo, useState } from 'react'
import { useAnalytics } from '~/hooks/useAnalytics'
import { AnalyticsNamespace, WebPhoneAnalyticsEvents } from '~/types/Analytics'
import { SpeedDial, UserSpeedDialMap } from '~/types/WebphoneTypes'
import { replacer, reviver } from '~/utils/maps'

import { StorageKeys } from './StorageKeys'
import { useUserState } from './UserState'

export enum CallMode {
  CLICK_TO_CALL = 'ClickToCall',
  WEBPHONE = 'Webphone',
}
export enum Ringtone {
  DEFAULT = 'default',
  SILENT = 'silent',
}
export enum DoNotDisturb {
  INDEFINITE = 0,
  TOMORROW = -1,
  HALF_HOUR = 0.5,
  ONE_HOUR = 1,
  TWO_HOURS = 2,
  FOUR_HOURS = 4,
  EIGHT_HOURS = 8,
}
export type ParkingLot = {
  id: number
  label: string
  defaultLabel: string
  targetNumber: string
}
export type Extension = {
  id: number
  name: string
  number: string
  uuid: string
}

interface ClickToCallSettings {
  shouldAutoAnswer: boolean
  clickToCallMode: CallMode
}
interface WebPhoneSettings {
  isEnabled: boolean
  doNotDisturbTimestamp: Date | null | undefined
  ringtone: Ringtone
  hasCallWaiting: boolean
  allowClickToCallIncoming: boolean
  enableCallerIdSelection: boolean
}
interface NotificationSettings {
  hasNewTextSound: boolean
}
interface CallCenterAgentSettings {
  isEnabled: boolean
}
interface FaxSettings {
  includeCoverPage: boolean
}
interface Settings {
  clickToCall: ClickToCallSettings
  webPhone: WebPhoneSettings
  notifications: NotificationSettings
  callCenter: CallCenterAgentSettings
  fax: FaxSettings
}
interface PbxSettings {
  parkingLots: ParkingLot[]
  extensions: Extension[]
}

const settingsDefault: Settings = {
  clickToCall: {
    shouldAutoAnswer: false,
    clickToCallMode: CallMode.WEBPHONE,
  },
  webPhone: {
    isEnabled: false,
    doNotDisturbTimestamp: undefined,
    ringtone: Ringtone.DEFAULT,
    hasCallWaiting: true,
    allowClickToCallIncoming: true,
    enableCallerIdSelection: false,
  },
  notifications: {
    hasNewTextSound: true,
  },
  callCenter: {
    isEnabled: false,
  },
  fax: {
    includeCoverPage: false,
  },
}
const settingsStateAtom = atomWithStorage<Settings>(
  StorageKeys.AppSettings,
  settingsDefault,
)
const pbxSettingsAtom = atomWithStorage<string>(
  StorageKeys.PbxAppSettings,
  JSON.stringify(new Map<number, PbxSettings>(), replacer),
)
const speedDialAtom = atomWithStorage<string>(
  StorageKeys.SpeedDial,
  JSON.stringify(new Map<number, UserSpeedDialMap>(), replacer),
)

// Utility function to merge settings
const mergeSettingsWithDefaults = (
  currentSettings: Settings,
  defaultSettings: Settings,
): Settings => {
  return {
    ...defaultSettings,
    ...currentSettings,
    clickToCall: {
      ...defaultSettings.clickToCall,
      ...currentSettings.clickToCall,
    },
    webPhone: {
      ...defaultSettings.webPhone,
      ...currentSettings.webPhone,
    },
    notifications: {
      ...defaultSettings.notifications,
      ...currentSettings.notifications,
    },
    callCenter: {
      ...defaultSettings.callCenter,
      ...currentSettings.callCenter,
    },
  }
}

export const useSettingsState = (pbxId?: number) => {
  const [settings, setSettings] = useAtom(settingsStateAtom)
  const [pbxSettingsEncoded, setPbxSettingsEncoded] = useAtom(pbxSettingsAtom)
  const [speedDialEncoded, setSpeedDialEncoded] = useAtom(speedDialAtom)
  const { sessionUser, currentUserExtension } = useUserState()
  const userSpeedDial = useMemo<UserSpeedDialMap>(
    () => JSON.parse(speedDialEncoded, reviver),
    [speedDialEncoded],
  )
  const speedDial = useMemo(() => {
    if (sessionUser?.id) {
      return userSpeedDial[sessionUser?.id] || new Map<number, SpeedDial>()
    }
    return new Map<number, SpeedDial>()
  }, [speedDialEncoded, sessionUser?.id])

  const updateSpeedDial = useCallback(
    (label: string, number: string, id?: number) => {
      if (sessionUser?.id) {
        const updatedUserSpeedDial = { ...userSpeedDial }
        if (!updatedUserSpeedDial[sessionUser?.id]) {
          updatedUserSpeedDial[sessionUser?.id] = new Map<number, SpeedDial>()
        }
        const value = { id: id ?? new Date().getTime(), label, number }
        updatedUserSpeedDial[sessionUser?.id].set(id ?? value.id, value)
        setSpeedDialEncoded(JSON.stringify(updatedUserSpeedDial, replacer))
      }
    },
    [speedDialEncoded, sessionUser?.id ?? ''],
  )

  const removeSpeedDial = useCallback(
    (key: number) => {
      if (sessionUser?.id) {
        const updatedUserSpeedDial = { ...userSpeedDial }
        if (updatedUserSpeedDial[sessionUser?.id]) {
          const updatedSpeedDial = new Map(
            updatedUserSpeedDial[sessionUser?.id],
          )
          updatedSpeedDial.delete(key)
          updatedUserSpeedDial[sessionUser?.id] = updatedSpeedDial
          setSpeedDialEncoded(JSON.stringify(updatedUserSpeedDial, replacer))
        }
      }
    },
    [speedDial, sessionUser?.id ?? ''],
  )

  const { track: webPhoneTrack } = useAnalytics(AnalyticsNamespace.WEB_PHONE)

  const [doNotDisturbTimeout, setDoNotDisturbTimeout] =
    useState<NodeJS.Timeout>()

  // Ensure settings are merged with defaults
  const mergedSettings = mergeSettingsWithDefaults(settings, settingsDefault)
  if (JSON.stringify(settings) !== JSON.stringify(mergedSettings)) {
    setSettings(mergedSettings)
  }

  //UX: Not sure if this should be a permanent change or just calculated on the return
  if (
    !settings.webPhone.isEnabled &&
    settings.clickToCall.clickToCallMode === CallMode.WEBPHONE
  ) {
    setSettings(
      produce((draft: Settings) => {
        draft.clickToCall.clickToCallMode = CallMode.CLICK_TO_CALL
      }),
    )
  }

  // #region PBX-dependent settings

  const pbxSettingsMap = new Map<number, PbxSettings>(
    JSON.parse(pbxSettingsEncoded, reviver),
  )
  const pbxSettings = pbxId
    ? pbxSettingsMap?.get(pbxId)
    : {
        parkingLots: [],
        extensions: [],
      }

  const setPbxSettingsMap = useCallback(
    (newMap: Map<number, PbxSettings>) => {
      setPbxSettingsEncoded(JSON.stringify(newMap, replacer))
    },
    [setPbxSettingsEncoded],
  )

  if (pbxId && pbxSettings?.parkingLots === undefined) {
    const initParking = [
      {
        id: 1,
        label: 'Parking 1',
        defaultLabel: 'Parking 1',
        targetNumber: '5901',
      },
      {
        id: 2,
        label: 'Parking 2',
        defaultLabel: 'Parking 2',
        targetNumber: '5902',
      },
    ]
    const newPbxSettings = new Map<number, PbxSettings>(pbxSettingsMap)
    newPbxSettings.set(pbxId, { parkingLots: initParking, extensions: [] })
    setPbxSettingsMap(newPbxSettings)
  }

  const addParkingLot = useCallback(
    (parkingLot: ParkingLot) => {
      if (!pbxId) {
        return parkingLot
      }
      const existingParkingLots = pbxSettings?.parkingLots ?? []
      existingParkingLots.push(parkingLot)
      existingParkingLots.sort((a, b) => a.label.localeCompare(b.label))

      const newPbxSettings = new Map<number, PbxSettings>(pbxSettingsMap)
      newPbxSettings.set(pbxId, {
        parkingLots: existingParkingLots,
        extensions: newPbxSettings.get(pbxId)?.extensions ?? [],
      })
      setPbxSettingsMap(newPbxSettings)
    },
    [pbxSettingsEncoded, pbxId],
  )
  const removeParkingLot = useCallback(
    (parkingLotId: number) => {
      if (!pbxId) {
        return parkingLotId
      }
      const existingParkingLots = pbxSettings?.parkingLots ?? []
      const newParkingLots = existingParkingLots.filter(
        (parkingLot) => parkingLot.id !== parkingLotId,
      )

      const newPbxSettings = new Map<number, PbxSettings>(pbxSettingsMap)
      newPbxSettings.set(pbxId, {
        parkingLots: newParkingLots,
        extensions: newPbxSettings.get(pbxId)?.extensions ?? [],
      })
      setPbxSettingsMap(newPbxSettings)
    },
    [pbxSettingsEncoded, pbxId],
  )

  const addTrackedExtension = useCallback(
    (extension: Extension) => {
      if (!pbxId) {
        return extension
      }
      const existingExtensions = pbxSettings?.extensions ?? []
      existingExtensions.push(extension)
      existingExtensions.sort((a, b) => a.name.localeCompare(b.name))

      const newPbxSettings = new Map<number, PbxSettings>(pbxSettingsMap)
      newPbxSettings.set(pbxId, {
        parkingLots: newPbxSettings.get(pbxId)?.parkingLots ?? [],
        extensions: existingExtensions,
      })
      setPbxSettingsMap(newPbxSettings)
    },
    [pbxSettingsEncoded, pbxId],
  )
  const removeTrackedExtension = useCallback(
    (extensionId: number) => {
      if (!pbxId) {
        return extensionId
      }
      const existingExtensions = pbxSettings?.extensions ?? []
      const newExtensions = existingExtensions.filter(
        (extension) => extension.id !== extensionId,
      )

      const newPbxSettings = new Map<number, PbxSettings>(pbxSettingsMap)
      newPbxSettings.set(pbxId, {
        parkingLots: newPbxSettings.get(pbxId)?.parkingLots ?? [],
        extensions: newExtensions,
      })
      setPbxSettingsMap(newPbxSettings)
    },
    [pbxSettingsEncoded, pbxId],
  )
  // #endregion

  // #region Click to Call
  const setShouldAutoAnswer = useCallback((shouldAutoAnswer: boolean) => {
    setSettings(
      produce((draft: Settings) => {
        draft.clickToCall.shouldAutoAnswer = shouldAutoAnswer
      }),
    )
  }, [])
  const setClickToCallMode = useCallback((clickToCallMode: CallMode) => {
    setSettings(
      produce((draft: Settings) => {
        draft.clickToCall.clickToCallMode = clickToCallMode
      }),
    )
  }, [])
  // #endregion

  // #region Web Phone
  const setWebPhoneEnabled = useCallback((isEnabled: boolean) => {
    setSettings(
      produce((draft: Settings) => {
        draft.webPhone.isEnabled = isEnabled
      }),
    )
  }, [])
  const canUseWebPhone = useMemo(() => {
    return settings.webPhone.isEnabled && currentUserExtension !== undefined
  }, [settings.webPhone.isEnabled, currentUserExtension])

  const setDoNotDisturb = useCallback(
    (doNotDisturbSetting: DoNotDisturb | undefined) => {
      webPhoneTrack(
        doNotDisturbSetting
          ? WebPhoneAnalyticsEvents.DndOn
          : WebPhoneAnalyticsEvents.DndOff,
        { hours: doNotDisturbSetting },
      )
      let newDoNotDisturbTimestamp: Date | null
      if (doNotDisturbSetting !== undefined) {
        if (doNotDisturbSetting === DoNotDisturb.TOMORROW) {
          const tomorrow = new Date()
          tomorrow.setDate(tomorrow.getDate() + 1)
          tomorrow.setHours(0, 0, 0, 0)
          newDoNotDisturbTimestamp = tomorrow
        } else if (doNotDisturbSetting === DoNotDisturb.INDEFINITE) {
          newDoNotDisturbTimestamp = null
        } else {
          const now = Date.now()
          const hoursInMilliseconds = 1000 * 60 * 60 * doNotDisturbSetting
          const futureDate = new Date(now + hoursInMilliseconds)
          newDoNotDisturbTimestamp = futureDate
        }

        if (newDoNotDisturbTimestamp) {
          setDoNotDisturbTimeout(
            setTimeout(() => {
              setDoNotDisturb(undefined)
            }, newDoNotDisturbTimestamp.getTime() - Date.now()),
          )
        }
      } else {
        clearTimeout(doNotDisturbTimeout)
      }
      setSettings(
        produce((draft: Settings) => {
          draft.webPhone.doNotDisturbTimestamp =
            doNotDisturbSetting === undefined
              ? undefined
              : newDoNotDisturbTimestamp
        }),
      )
    },
    [doNotDisturbTimeout],
  )
  const setRingtone = useCallback((ringtone: Ringtone) => {
    setSettings(
      produce((draft: Settings) => {
        draft.webPhone.ringtone = ringtone
      }),
    )
  }, [])
  const setHasCallWaiting = useCallback((hasCallWaiting: boolean) => {
    setSettings(
      produce((draft: Settings) => {
        draft.webPhone.hasCallWaiting = hasCallWaiting
      }),
    )
  }, [])
  const setAllowClickToCallIncoming = useCallback(
    (allowClickToCallIncoming: boolean) => {
      setSettings(
        produce((draft: Settings) => {
          draft.webPhone.allowClickToCallIncoming = allowClickToCallIncoming
        }),
      )
    },
    [],
  )
  const setEnableCallerIdSelection = useCallback(
    (enableCallerIdSelection: boolean) => {
      setSettings(
        produce((draft: Settings) => {
          draft.webPhone.enableCallerIdSelection = enableCallerIdSelection
        }),
      )
    },
    [],
  )
  // #endregion

  // #region Notifications
  const setHasNewTextSound = useCallback((hasNewTextSound: boolean) => {
    setSettings(
      produce((draft: Settings) => {
        draft.notifications.hasNewTextSound = hasNewTextSound
      }),
    )
  }, [])
  // #endregion

  // #region Call Center Agent
  const setCallCenterAgentEnabled = useCallback((isEnabled: boolean) => {
    setSettings(
      produce((draft: Settings) => {
        draft.callCenter.isEnabled = isEnabled
      }),
    )
  }, [])
  // #endregion

  const setFaxCoverPageEnabled = useCallback((isEnabled: boolean) => {
    setSettings(
      produce((draft: Settings) => {
        draft.fax.includeCoverPage = isEnabled
      }),
    )
  }, [])
  return {
    settings: { ...settings, pbx: { ...pbxSettings }, speedDial },
    canUseWebPhone,
    addParkingLot,
    removeParkingLot,
    addTrackedExtension,
    removeTrackedExtension,
    setShouldAutoAnswer,
    setClickToCallMode,
    setWebPhoneEnabled,
    setDoNotDisturb,
    setRingtone,
    setHasCallWaiting,
    setAllowClickToCallIncoming,
    setEnableCallerIdSelection,
    setHasNewTextSound,
    setCallCenterAgentEnabled,
    updateSpeedDial,
    removeSpeedDial,
    setFaxCoverPageEnabled,
  }
}
